/**
 * Dashboard — the organiser's register.
 *
 * Per Craig's Priority 3 lock: this is the curator workspace, not a
 * SaaS event-manager. The layout reads as a museum acquisition
 * ledger / luxury hotel operations desk — horizontal rhythm, compact
 * confidence, calm spacing.
 *
 * What this is NOT:
 *   • a card grid
 *   • a metrics panel
 *   • a Notion-shaped "events" list
 *   • a CMS
 *
 * What this IS:
 *   • a parchment-on-graphite register, one row per preserved night
 *   • state glyph + name + when + a single editorial summary phrase
 *   • restrained operational metadata in tabular ALL-CAPS sub-line
 *   • quiet "Open ✦" affordance, no launch-button stack
 *
 * The feature-launch buttons that used to live on every card are
 * pushed inside EventPage (Phase 4 — next session). The dashboard's
 * job is to present the register, not to be the control panel.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { setPreferredView } from '../utils/postAuthRoute';
import {
  MODE_TWO,
  PageShell, PageGutter,
  Eyebrow, EditorialHeading,
  GraphitePanel, MutedDivider,
  StateGlyph, StateLabel,
  QuietPress,
  deriveEventState,
  editorialPhrase,
  operationalLine,
} from '../components/ModeTwoPrimitives';
import { EventCompanionsLedgerStrip } from '../components/EventCompanions';
import useRoomPulse from '../lib/useRoomPulse';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VIEW_KEY = 'eventdrop_view';
const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };

// ── Date formatter — operational, restrained, no relative gimmicks ─
function formatEventDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [outcomes, setOutcomes] = useState({});
  const [reflections, setReflections] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasSupplierProfile, setHasSupplierProfile] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', { state: { from: '/dashboard' } });
      return;
    }
    const viewParam = searchParams.get('view');
    if (viewParam === 'host' || viewParam === 'supplier') {
      setPreferredView(viewParam);
      if (viewParam === 'supplier') {
        navigate('/dashboard/supplier', { replace: true });
        return;
      }
      fetchEvents();
      axios.get(`${API}/suppliers/me`)
        .then(r => setHasSupplierProfile(!!r.data?.supplier?.category))
        .catch(() => {});
      return;
    }
    axios.get(`${API}/suppliers/me`)
      .then(r => {
        const isSupplier = !!r.data?.supplier?.category;
        setHasSupplierProfile(isSupplier);
        if (!isSupplier) { fetchEvents(); return; }
        axios.get(`${API}/events`)
          .then(er => {
            const list = Array.isArray(er?.data) ? er.data : (er?.data?.events || []);
            const hasEvents = list.length > 0;
            const stored = safeGet(VIEW_KEY);
            if (!hasEvents) {
              navigate('/dashboard/supplier', { replace: true });
              return;
            }
            if (stored === 'supplier') {
              navigate('/dashboard/supplier', { replace: true });
              return;
            }
            setEvents(list);
            setLoading(false);
            axios.get(`${API}/events/me/outcomes`)
              .then(oc => {
                const byId = {};
                (oc.data || []).forEach((row) => { byId[row.event_id] = row; });
                setOutcomes(byId);
              }).catch(() => {});
            axios.get(`${API}/events/me/reflections`)
              .then(rr => {
                const byId = {};
                (rr.data || []).forEach((row) => {
                  if (row && row.reflection) byId[row.event_id] = row.reflection;
                });
                setReflections(byId);
              }).catch(() => {});
          })
          .catch(() => fetchEvents());
      })
      .catch(() => fetchEvents());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const fetchEvents = async () => {
    try {
      const pendingEventId = localStorage.getItem('pending_event_id');
      if (pendingEventId) {
        try { await axios.post(`${API}/events/claim`, { event_id: pendingEventId }); }
        catch (e) { /* ignore */ }
        localStorage.removeItem('pending_event_id');
      }
      try { await axios.post(`${API}/events/claim-unclaimed`); }
      catch (e) { /* ignore */ }
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
      try {
        const oc = await axios.get(`${API}/events/me/outcomes`);
        const byId = {};
        (oc.data || []).forEach((row) => { byId[row.event_id] = row; });
        setOutcomes(byId);
      } catch { /* ignore */ }
      try {
        const rr = await axios.get(`${API}/events/me/reflections`);
        const byId = {};
        (rr.data || []).forEach((row) => {
          if (row && row.reflection) byId[row.event_id] = row.reflection;
        });
        setReflections(byId);
      } catch { /* ignore */ }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login?logged_out=true');
  };

  if (authLoading || loading) {
    return (
      <PageShell>
        <PageGutter>
          <div data-testid="dashboard-loading" style={{
            padding: '120px 0', textAlign: 'center',
            fontFamily: MODE_TWO.serif, fontStyle: 'italic',
            fontSize: 14, color: MODE_TWO.inkMuted,
          }}>Opening the register…</div>
        </PageGutter>
      </PageShell>
    );
  }

  return (
    <PageShell testId="dashboard-shell">
      <PageGutter>

        {/* ── HEADER ─────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', flexWrap: 'wrap',
          justifyContent: 'space-between', alignItems: 'flex-end',
          gap: 20, marginBottom: 'clamp(28px, 4vw, 44px)',
        }}>
          <div>
            <a href="/" style={{
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 13, color: MODE_TWO.inkMuted,
              textDecoration: 'none', letterSpacing: '0.04em',
            }} data-testid="dashboard-logo-link">← EventDrop</a>
            <div style={{ marginTop: 18 }}>
              <Eyebrow>✦ The Register</Eyebrow>
            </div>
            <div style={{ marginTop: 10 }}>
              <EditorialHeading size="h1">
                <span data-testid="dashboard-title">Your preserved nights</span>
              </EditorialHeading>
            </div>
            <div data-testid="user-greeting" style={{
              marginTop: 10, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 13.5, color: MODE_TWO.inkMuted, letterSpacing: '0.005em',
            }}>
              Welcome back, {user?.name || 'curator'}.
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {hasSupplierProfile && (
              <QuietPress
                variant="quiet"
                onClick={() => { setPreferredView('supplier'); navigate('/dashboard/supplier'); }}
                testId="switch-to-supplier"
              >Supplier desk</QuietPress>
            )}
            {/* EventDrop Pro ✦ — internal multi-event booth operator
                entry. Only renders for users with the is_pro_operator
                flag (set manually via seed_pro_operator.py). Non-Pro
                hosts never see this pill — the feature is invisible. */}
            {user?.is_pro_operator && (
              <QuietPress
                variant="quiet"
                onClick={() => navigate('/pro')}
                testId="open-pro-sessions"
              >Booth Sessions ✦</QuietPress>
            )}
            <QuietPress
              variant="quiet"
              onClick={() => navigate('/admin')}
              testId="admin-button"
            >Operator tools</QuietPress>
            <QuietPress
              variant="quiet"
              onClick={handleLogout}
              testId="logout-button"
            >Sign out</QuietPress>
            <QuietPress
              onClick={() => navigate('/create')}
              testId="create-event-button"
            >Begin a new night ✦</QuietPress>
          </div>
        </div>

        {/* ── DUAL ROLE NOTE — quiet line, not a banner ───────────── */}
        {hasSupplierProfile && (
          <div data-testid="dual-role-supplier-card" style={{
            marginBottom: 22,
            padding: '12px 16px',
            background: MODE_TWO.bgRaised,
            border: `1px solid ${MODE_TWO.hairSoft}`,
            borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 14, flexWrap: 'wrap',
          }}>
            <div style={{
              fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 13.5, color: MODE_TWO.inkMid,
            }}>
              You also keep a supplier desk. <button
                type="button"
                onClick={() => { setPreferredView('supplier'); navigate('/dashboard/supplier'); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: MODE_TWO.gold, fontFamily: 'inherit', fontStyle: 'inherit',
                  fontSize: 'inherit', textDecoration: 'underline',
                  textDecorationColor: MODE_TWO.goldHair, textUnderlineOffset: 4,
                  padding: 0,
                }}
                data-testid="dual-role-supplier-link"
              >Step over ✦</button>
            </div>
          </div>
        )}

        {/* ── EMPTY STATE ─────────────────────────────────────────── */}
        {events.length === 0 ? (
          <GraphitePanel testId="no-events-message" padding="clamp(40px, 5vw, 64px)">
            <div style={{ textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
              <Eyebrow>✦ The register is quiet</Eyebrow>
              <div style={{ marginTop: 14 }}>
                <EditorialHeading size="h2">No nights preserved yet.</EditorialHeading>
              </div>
              <p style={{
                marginTop: 14, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                fontSize: 14.5, color: MODE_TWO.inkMuted, lineHeight: 1.6,
              }}>
                Begin a night. The register fills out one preserved memory at a time.
              </p>
              <div style={{ marginTop: 22 }}>
                <QuietPress
                  onClick={() => navigate('/create')}
                  testId="empty-create-event-button"
                >Begin a new night ✦</QuietPress>
              </div>
            </div>
          </GraphitePanel>
        ) : (
          // ── LEDGER ───────────────────────────────────────────────
          <div data-testid="events-grid" style={{
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <RegisterHeader />
            {events.map((event) => (
              <LedgerRow
                key={event.event_id}
                event={event}
                outcome={outcomes[event.event_id]}
                reflection={reflections[event.event_id]}
                onOpen={() => navigate(`/event/${event.slug}`)}
              />
            ))}
          </div>
        )}
      </PageGutter>
    </PageShell>
  );
};


// ── RegisterHeader — the ledger column titles, restrained ─────────
const RegisterHeader = () => (
  <div data-testid="ledger-header" style={{
    display: 'grid',
    gridTemplateColumns: 'minmax(280px, 1.4fr) minmax(120px, 0.7fr) minmax(0, 1.5fr) auto',
    gap: 18, alignItems: 'center',
    padding: '8px 22px 10px',
    borderBottom: `1px solid ${MODE_TWO.hairSoft}`,
  }}>
    <RegisterColTitle>The Night</RegisterColTitle>
    <RegisterColTitle>State</RegisterColTitle>
    <RegisterColTitle>What unfolded</RegisterColTitle>
    <RegisterColTitle align="right">Open</RegisterColTitle>
  </div>
);

const RegisterColTitle = ({ children, align }) => (
  <div style={{
    fontFamily: MODE_TWO.sans,
    fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
    color: MODE_TWO.inkFaint,
    textAlign: align || 'left',
  }}>{children}</div>
);


// ── LedgerRow — a single preserved night, presented as a register row
const LedgerRow = ({ event, outcome, reflection, onOpen }) => {
  const navigate = useNavigate();
  const state = deriveEventState(event);
  const phraseStats = outcome?.stats || {};
  // When the lifecycle observation resolver has surfaced a
  // reflection for this event, it REPLACES the static phrase in
  // column 3 (per the constitution: "the editorial-phrase column
  // can host an observation"). When no reflection is active the
  // row returns to the static observational phrase. No "new"
  // chrome, no badge, no count — just the line.
  const hasReflection = !!(reflection && reflection.copy);
  // Quiet whisper — only for gathering/returning rows, and only
  // when BOTH a kiosk is awake AND a recent memory transition has
  // landed. Dormant by default; when active, takes priority over
  // every other line in column 3 because it represents the most
  // present truth about the night. Same words, same windows as
  // Tonight ✦ — the two surfaces never disagree.
  const pulse = useRoomPulse(event);
  const hasPulse = !!(pulse && pulse.alive && pulse.phrase);
  return (
    <GraphitePanel
      testId={`event-card-${event.slug}`}
      padding="18px 22px"
      style={{
        cursor: 'pointer',
        transition: 'border-color 240ms ease, background 240ms ease',
      }}
    >
      <div
        onClick={onOpen}
        className="mode2-ledger-row"
        style={{
          display: 'grid',
          // 4-column desktop layout. Column 1 uses minmax(0, ...) so
          // a long event name wraps cleanly inside the cell instead
          // of pushing the grid wider than the viewport (Craig flagged
          // 2026-05-13 — mobile was overflowing horizontally). The
          // .mode2-ledger-row CSS rule below collapses this into a
          // single-column stack on narrow screens.
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 0.7fr) minmax(0, 1.5fr) auto',
          gap: 18, alignItems: 'center',
        }}
      >
        {/* Column 1 — name + date */}
        <div style={{ minWidth: 0 }}>
          <h3 data-testid={`event-name-${event.slug}`} style={{
            margin: 0, fontFamily: MODE_TWO.serif, fontWeight: 500,
            fontSize: 17.5, color: MODE_TWO.ink, letterSpacing: '-0.005em',
            lineHeight: 1.3, overflowWrap: 'anywhere',
          }}>{event.event_name}</h3>
          <div style={{
            marginTop: 4, display: 'flex', alignItems: 'baseline', gap: 10,
            fontFamily: MODE_TWO.sans, fontSize: 11.5, letterSpacing: '0.06em',
            color: MODE_TWO.inkFaint, textTransform: 'uppercase',
          }}>
            <span data-testid={`event-date-${event.slug}`}>{formatEventDate(event.event_date)}</span>
            {/* Tier metadata, not an upsell. The taxonomy is immutable
                for the lifetime of the event and reads as operational
                ledger metadata (LEGACY / STANDARD / PREMIUM etc.). */}
            <span data-testid={`event-package-${event.slug}`} style={{ color: MODE_TWO.goldSoft }}>
              {(event.package_type || 'standard').toUpperCase()}
            </span>
          </div>
          {/* Companions strip — the active products/companions for
              this event, rendered as restrained chips beneath the
              row's identity line. Lives only in column 1 so the
              register rhythm isn't disturbed. */}
          <EventCompanionsLedgerStrip
            event={event}
            testId={`event-companions-strip-${event.slug}`}
          />
        </div>

        {/* Column 2 — state glyph + label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <StateGlyph state={state} testId={`event-state-glyph-${event.slug}`} />
          <StateLabel state={state} testId={`event-state-label-${event.slug}`} />
        </div>

        {/* Column 3 — pulse whisper > editorial phrase > active reflection */}
        <div data-testid={`event-phrase-${event.slug}`} style={{
          minWidth: 0,
          fontFamily: MODE_TWO.serif, fontStyle: 'italic',
          fontSize: 13.5, color: MODE_TWO.inkMid,
          lineHeight: 1.5, letterSpacing: '0.005em',
          overflowWrap: 'anywhere',
        }}>
          {hasPulse ? (
            <span
              data-testid={`event-room-pulse-${event.slug}`}
              data-pulse-state="alive"
              style={{ color: MODE_TWO.ink }}
            >{pulse.phrase}</span>
          ) : hasReflection ? (
            <span
              data-testid={`event-reflection-${event.slug}`}
              data-reflection-category={reflection.category}
              style={{ color: MODE_TWO.ink }}
            >{reflection.copy}</span>
          ) : outcome ? (
            <>
              {editorialPhrase(phraseStats) || 'The room is still being prepared ✦'}
              {operationalLine(phraseStats) && (
                <div style={{
                  marginTop: 4, fontFamily: MODE_TWO.sans, fontStyle: 'normal',
                  fontSize: 10.5, letterSpacing: '0.08em',
                  color: MODE_TWO.inkFaint, textTransform: 'uppercase',
                }}>{operationalLine(phraseStats)}</div>
              )}
            </>
          ) : (
            <span style={{ color: MODE_TWO.inkFaint }}>The room is still being prepared ✦</span>
          )}
        </div>

        {/* Column 4 — open + (optional) share */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <QuietPress
            variant="quiet"
            onClick={onOpen}
            testId={`manage-${event.slug}`}
          >Open ✦</QuietPress>
          {/* Pair Booth ✦ — surfaces only when Booth OS is part of the
              package. Deep-links the host straight to the Setup tab
              with the pairing code soft-pulsing, saving 2-3 clicks
              of scroll on a real setup night. Operationally critical
              right before doors open. */}
          {event.booth_os_purchased && (
            <QuietPress
              variant="quiet"
              onClick={() => navigate(`/event/${event.slug}?focus=pairing`)}
              testId={`pair-booth-${event.slug}`}
            >Pair Booth ✦</QuietPress>
          )}
          {outcome && (outcome.stats?.photos >= 3 || outcome.stats?.shares > 0) && (
            <ShareAffordance event={event} outcome={outcome} />
          )}
        </div>
      </div>
    </GraphitePanel>
  );
};


// ── ShareAffordance — single quiet pill on the row, never a card ──
const ShareAffordance = ({ event, outcome }) => {
  const handleShare = async () => {
    const url = `${window.location.origin}/e/${event.slug}`;
    let succeeded = false;
    try {
      if (navigator.share) {
        await navigator.share({ title: outcome.event_name, url });
        succeeded = true;
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        succeeded = true;
      }
    } catch (_) {/* user cancelled */}
    if (succeeded && outcome.event_id) {
      axios.post(`${API}/events/${outcome.event_id}/track-share`, { ref: 'host' }).catch(() => {});
    }
  };
  return (
    <QuietPress
      variant="quiet"
      onClick={handleShare}
      testId={`outcome-share-btn-${outcome.event_id}`}
    >Share the wall ✦</QuietPress>
  );
};


export default Dashboard;
